import { useEffect, useState } from 'react';
import { Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { Provider } from 'react-redux';
import { useLanguageSync } from './hooks/useLanguageSync';
import { useTranslation } from 'react-i18next';
import { Navigate, useParams } from 'react-router-dom';
import { store } from './store';
import axios from 'axios';
import { HelmetProvider } from 'react-helmet-async';

import { WorkProvider } from './context/WorkContext';
import Layout from './Layout';
import MaintenanceWrapper from './components/Maintenance/Maintenance';
import Blog from './pages/Blog';
import ArticlePage from './pages/ArticlePage';

import ErrorBoundary from './components/ErrorBoundary/ErrorBoundary';
import UnsubscribePage from './pages/UnsubscribePage';

import Home from './pages/Home';
import About from './pages/About';
import Careers from './pages/Careers';
import PositionPage from './pages/PositionPage';
import Contact from './pages/Contact';
import ContactPromoWrapper from './hooks/ContactPromoWrapper';
import ContactPromoGeneralWrapper from './hooks/ContactPromoGeneralWrapper';
import UxDesign from './pages/UxDesign';
import WebDesign from './pages/WebDesign';
import WebDevelopment from './pages/WebDevelopment';
import CRO from './pages/CRO';
import SEOPage from './pages/SEOPage';
import Marketing from './pages/Marketing';
import MarketingItem from './components/MarketingItem/MarketingItem';
import Ecommerce from './pages/Ecommerce';
import BrandIdentity from './pages/BrandIdentity';
import LogoDesign from './pages/LogoDesign';
import GraphicDesign from './pages/GraphicDesign';
import B2B from './pages/B2B';
import B2C from './pages/B2C';
import ServicesCompanies from './pages/ServicesCompanies';
import Faqs from './pages/Faqs';
import Work from './pages/Work';
import WorkProjectPage from './pages/WorkProjectPage';
import ErrorPage from './pages/404Page';
import ScrollToTop from './components/ScrollToTop';
import Sitemap from './pages/Sitemap';
import Impressum from './pages/Impressum';

import './index.scss';
import PrivacyPolicy from './pages/PrivacyPolicy';
import PrivacyPolicyPopup from './pages/PrivacyPolicyPopup';
import Terms from './pages/Terms';
import ComingSoonPage from './pages/ComingSoonPage';
import PromoPage from './pages/PromoPage';

import LanguageGuard from './i18n/components/LanguageGuard/LanguageGuard';
import LanguageValidator from './i18n/components/LanguageValidator/LanguageValidator';
import CookieBanner from './components/CookieBanner/CookieBanner';

const App = () => {

  useLanguageSync();

  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (location.pathname === '/') {
      const savedLang = localStorage.getItem('preferredLanguage') || 'ua';
      navigate(`/${savedLang}`, { replace: true });
    }
  }, [location.pathname, navigate]);

  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const [maintenanceUntil, setMaintenanceUntil] = useState(null);
  const [hideHeaderFooter, setHideHeaderFooter] = useState(false);
  const [hideLanguageSwitcher, setHideLanguageSwitcher] = useState(false);

  useEffect(() => {
    const handler = (e) => { if (e.target.tagName === "IMG") e.preventDefault(); };
    document.addEventListener("contextmenu", handler);
    return () => document.removeEventListener("contextmenu", handler);
  }, []);


  useEffect(() => {
    let intervalId;

    const checkServerStatus = async () => {
      try {
        const apiUrl = process.env.NODE_ENV === 'development'
          ? 'http://localhost:5001/api/status'
          : 'https://carabinex.onrender.com/api/status';

        const response = await axios.get(apiUrl);

        if (maintenanceMode && !response.data.maintenance) {
          window.location.reload();
          return;
        }

        setMaintenanceMode(response.data.maintenance);
        setMaintenanceUntil(response.data.maintenanceUntil);

        if (intervalId) {
          clearInterval(intervalId);
          const newInterval = response.data.maintenance ? 10000 : 20000;
          intervalId = setInterval(checkServerStatus, newInterval);
        }

      } catch (err) {
        console.error("Failed to check status", err);
      }
    };

    checkServerStatus();
    intervalId = setInterval(checkServerStatus, 30000);

    return () => clearInterval(intervalId);
  }, [maintenanceMode]);

  const PromoRedirect = () => {
    const { i18n } = useTranslation();
    const { slug } = useParams();
    const currentLang = i18n.language; // уже определён правильно
    return <Navigate to={`/${currentLang}/promo/${slug}`} replace />;
  };

  return (
    <HelmetProvider>
      <Provider store={store}>
        <WorkProvider>
          <ErrorBoundary
            showDetails={process.env.NODE_ENV === 'development'}
            onReset={() => {
              if (process.env.NODE_ENV === 'development') {
                console.log('Error boundary reset');
              }
            }}
            customMessage={{
              title: "Something went wrong",
              subtitle: "We're working to fix the issue. Please try again later."
            }}
          >
            <MaintenanceWrapper
              maintenanceMode={maintenanceMode}
              maintenanceEndTime={maintenanceUntil ? new Date(maintenanceUntil).getTime() : null}>
              <ScrollToTop />
              <Routes>
                <Route path="/promo/:slug" element={<PromoRedirect />} />
                <Route path="/:lang/privacy-policy-popup" element={<PrivacyPolicyPopup />} />
                <Route path="/blog" element={<Blog hideHeaderFooter={hideHeaderFooter} hideLanguageSwitcher={hideLanguageSwitcher} />} />
                <Route path="/blog/:categorySlug" element={<Blog hideHeaderFooter={hideHeaderFooter} hideLanguageSwitcher={hideLanguageSwitcher} />} />

                <Route path="/:lang/blog" element={<Blog hideHeaderFooter={hideHeaderFooter} hideLanguageSwitcher={hideLanguageSwitcher} />} />
                <Route path="/:lang/blog/:categorySlug" element={<Blog hideHeaderFooter={hideHeaderFooter} hideLanguageSwitcher={hideLanguageSwitcher} />} />

                <Route path="/blog/:categorySlug/:articleTitle" element={<ArticlePage hideHeaderFooter={hideHeaderFooter} hideHeaderFooterSetter={setHideHeaderFooter} />} />
                <Route path="/:lang/blog/:categorySlug/:articleTitle" element={<ArticlePage hideHeaderFooter={hideHeaderFooter} hideHeaderFooterSetter={setHideHeaderFooter} />} />

                <Route path="/:lang/unsubscribe/*" element={<UnsubscribePage />} />

                <Route path="/:lang" element={
                  <LanguageValidator hideHeaderFooterSetter={setHideHeaderFooter} hideLanguageSwitcherSetter={setHideLanguageSwitcher}>
                    <LanguageGuard>
                      <Layout hideHeaderFooter={hideHeaderFooter} hideLanguageSwitcher={hideLanguageSwitcher} />
                    </LanguageGuard>
                  </LanguageValidator>
                }>
                  <Route index element={<Home />} />
                  <Route path="about" element={<About />} />
                  <Route path="careers" element={<Careers />} />
                  <Route path="careers/:slug" element={<PositionPage hideHeaderFooterSetter={setHideHeaderFooter} />} />
                  <Route path="contact" element={<Contact />} />
                  <Route path="promo/:slug/form" element={<ContactPromoWrapper hideHeaderFooterSetter={setHideHeaderFooter} hideLanguageSwitcherSetter={setHideLanguageSwitcher} />} />
                  <Route path="promo/:slug/form-general" element={<ContactPromoGeneralWrapper hideHeaderFooterSetter={setHideHeaderFooter} hideLanguageSwitcherSetter={setHideLanguageSwitcher} />} />
                  <Route path="sitemap" element={<Sitemap />} />
                  <Route path='privacy-policy' element={<PrivacyPolicy />} />
                  <Route path='terms-of-conditions' element={<Terms />} />
                  <Route path="impressum" element={<Impressum />} />
                  <Route path='coming-soon' element={<ComingSoonPage hideHeaderFooterSetter={setHideHeaderFooter} hideLanguageSwitcherSetter={setHideLanguageSwitcher} />} />
                  <Route path="promo/:slug" element={<PromoPage hideHeaderFooterSetter={setHideHeaderFooter} hideLanguageSwitcherSetter={setHideLanguageSwitcher} />} />

                  <Route path="ui-ux-design" element={<UxDesign />} />
                  <Route path="web-design" element={<WebDesign />} />
                  <Route path="web-development" element={<WebDevelopment />} />
                  <Route path="cro" element={<CRO />} />
                  <Route path="seo" element={<SEOPage />} />
                  <Route path="e-commerce" element={<Ecommerce />} />
                  <Route path="brand-image" element={<BrandIdentity />} />
                  <Route path="logo-design" element={<LogoDesign />} />
                  <Route path="graphic-design" element={<GraphicDesign />} />

                  <Route path="business-to-business" element={<B2B />} />
                  <Route path="business-to-consumer" element={<B2C />} />
                  <Route path="professional-services" element={<ServicesCompanies />} />

                  <Route path="marketing-and-advertising" element={<Marketing page="MarketingPage" />} />
                  <Route path="marketing-and-advertising/:pageSlug" element={<MarketingItem hideHeaderFooterSetter={setHideHeaderFooter} />} />

                  <Route path="faqs" element={<Faqs />} />

                  <Route path="work" element={<Work />} />
                  <Route path="work/:mainTitle" element={<WorkProjectPage hideHeaderFooterSetter={setHideHeaderFooter} />} />

                  <Route path="*" element={<ErrorPage hideHeaderFooterSetter={setHideHeaderFooter} />} />
                </Route>

                <Route path="*" element={<ErrorPage hideHeaderFooterSetter={setHideHeaderFooter} />} />
                <Route path="/404" element={<ErrorPage hideHeaderFooterSetter={setHideHeaderFooter} />} />
              </Routes>
              <CookieBanner />
            </MaintenanceWrapper>
          </ErrorBoundary>
        </WorkProvider>
      </Provider>
    </HelmetProvider>
  );
};

export default App;